import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Check, Sparkles, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignupPage() {
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [chosenPlan, setChosenPlan] = useState("trial");
  const [form, setForm] = useState({
    org_name: "",
    property_name: "",
    property_address: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    axios.get(`${API}/onboarding/plans`).then((r) => setPlans(r.data.plans || []));
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/onboarding/create-org`, {
        ...form,
        plan_id: chosenPlan,
      }, { withCredentials: true });
      // The backend set the httpOnly access_token cookie in the response.
      // refresh() will read /auth/me via that cookie.
      await refresh();
      toast.success(`Welcome to Prestigious, ${data.user.name.split(" ")[0]}!`);
      // If a paid plan was chosen, take them to Settings so they can complete Stripe checkout
      const dest = chosenPlan === "trial" ? "/admin/dashboard" : "/admin/settings?billing=start";
      navigate(dest);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2.message;
      toast.error(typeof msg === "string" ? msg : "Signup failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] grid-lines-dark text-white">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-3" data-testid="signup-logo">
            <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-11 w-11" />
          </Link>
          <div className="text-sm text-zinc-400">
            Have an account? <Link to="/login" className="text-white underline" data-testid="signup-login-link">Log in</Link>
          </div>
        </div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <div className="overline mb-2 flex items-center gap-2">
            <Sparkles className="h-3 w-3 text-indigo-400" /> 14-day free trial - no credit card
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold font-display tracking-tight">
            Get your first HOA live<br />in under 3 minutes.
          </h1>
          <p className="text-zinc-400 mt-3 max-w-lg">
            Multi-tenant, GHL-synced, DocuSign-ready. Every seat you invite,
            every dollar you collect, every board packet you send lives in one console.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
          {/* Plan picker */}
          <Card className="bg-[#0a0a0b] border-white/10 p-6" data-testid="signup-plans">
            <div className="overline mb-4">Pick a plan</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {plans.length === 0 && <div className="text-sm text-zinc-500">Loading plans...</div>}
              {plans.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`signup-plan-${p.id}`}
                  onClick={() => setChosenPlan(p.id)}
                  className={
                    "text-left rounded-lg border p-4 transition " +
                    (chosenPlan === p.id
                      ? "border-orange-500/60 bg-orange-500/[0.08] shadow-[inset_0_0_0_1px_rgba(249,115,22,0.35)]"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]")
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-semibold font-display">{p.name}</div>
                    {p.highlight && (
                      <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10">
                        Popular
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{p.tagline}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-bold">
                      ${(p.monthly_price_cents / 100).toFixed(0)}
                    </span>
                    <span className="text-xs text-zinc-500">/ month</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {p.seat_limit >= 100000 ? "Unlimited seats" : `${p.seat_limit} seats`}
                    {" · "}
                    {p.property_limit >= 10000 ? "unlimited properties" : `${p.property_limit} properties`}
                  </div>
                  <ul className="mt-3 space-y-1">
                    {(p.features || []).slice(0, 4).map((f, i) => (
                      <li key={i} className="text-xs text-zinc-300 flex items-center gap-1.5">
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-4">
              Every paid plan starts with a 14-day trial. You can upgrade or cancel from Settings anytime.
            </div>
          </Card>

          {/* Form */}
          <Card className="bg-[#0a0a0b] border-white/10 p-6" data-testid="signup-form">
            <div className="overline mb-4">Create your workspace</div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">Company / HOA name</Label>
                <Input required data-testid="signup-org-name" value={form.org_name} onChange={setField("org_name")} placeholder="Skyline Residences" />
              </div>
              <div>
                <Label className="text-xs">First property name</Label>
                <Input required data-testid="signup-property-name" value={form.property_name} onChange={setField("property_name")} placeholder="245 Riverside" />
              </div>
              <div>
                <Label className="text-xs">Property address</Label>
                <Input required data-testid="signup-property-address" value={form.property_address} onChange={setField("property_address")} placeholder="245 Riverside Blvd, New York, NY" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Your name</Label>
                  <Input required data-testid="signup-admin-name" value={form.admin_name} onChange={setField("admin_name")} placeholder="Alex Morgan" />
                </div>
                <div>
                  <Label className="text-xs">Your role</Label>
                  <Input value="Owner / Admin" disabled className="opacity-70" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Work email</Label>
                <Input required type="email" data-testid="signup-admin-email" value={form.admin_email} onChange={setField("admin_email")} placeholder="you@company.com" />
              </div>
              <div>
                <Label className="text-xs">Password (8+ chars)</Label>
                <Input required type="password" minLength={8} data-testid="signup-admin-password" value={form.admin_password} onChange={setField("admin_password")} />
              </div>
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium h-11 rounded-full"
                  data-testid="signup-submit"
                >
                  {busy ? "Creating..." : (
                    <>Start free trial <ArrowRight className="h-4 w-4 ml-1.5" /></>
                  )}
                </Button>
                <div className="text-[11px] text-zinc-500 mt-2 text-center">
                  By continuing you agree to Prestigious HOA Portal Terms &amp; Privacy.
                </div>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
